import { ApolloClient, ApolloQueryResult, ObservableQuery, OperationVariables, QueryOptions } from '@apollo/client';

interface QueryStoreRecord<TData, TVariables extends OperationVariables = OperationVariables> {
  query: ObservableQuery<TData, TVariables>;
  observable: Observable<ApolloQueryResult<TData>>;
  subscription: { unsubscribe: () => void };
}

export class SuspenseQueryManager {
  private store: Map<string, QueryStoreRecord<unknown, OperationVariables>> = new Map();

  getQuery<TData, TVariables extends OperationVariables>(id: string): ObservableQuery<TData, TVariables> | undefined {
    return this.store.get(id)?.query as ObservableQuery<TData, TVariables> | undefined;
  }

  getObservable<TData>(id: string): Observable<ApolloQueryResult<TData>> | undefined {
    return this.store.get(id)?.observable as Observable<ApolloQueryResult<TData>> | undefined;
  }

  getOrCreateRecord<TData, TVariables extends OperationVariables>(
    id: string,
    apolloClient: ApolloClient<unknown>,
    options: QueryOptions<TVariables, TData>
  ): QueryStoreRecord<TData, TVariables> {
    const existingRecord = this.store.get(id);
    if (existingRecord) {
      return existingRecord as QueryStoreRecord<TData, TVariables>;
    }

    const query = apolloClient.watchQuery(options);
    const observable = new Observable<ApolloQueryResult<TData>>();
    const subscription = query.subscribe(
      (result: ApolloQueryResult<TData>) => observable.notify(result),
      (error: Error) => observable.notifyError(error)
    );
    const record = {
      query: query as ObservableQuery<TData, TVariables>,
      observable,
      subscription: { unsubscribe: () => subscription.unsubscribe() },
    };
    this.store.set(id, record as QueryStoreRecord<unknown, OperationVariables>);
    return record;
  }

  deleteById(id: string): void {
    this.store.get(id)?.subscription?.unsubscribe();
    this.store.delete(id);
  }
}

type Result<T> = { success: true; data: T } | { success: false; error: Error };

class Observable<T> {
  private result: Result<T> | null = null;
  private listeners: Array<(value: Result<T>) => void> = [];

  subscribe(listener: (value: Result<T>) => void): void {
    this.listeners.push(listener);
  }

  unsubscribe(listener: (value: Result<T>) => void): void {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  notify(value: T): void {
    const result = { success: true, data: value } as const;
    this.result = result;
    this.listeners.forEach((listener) => listener(result));
  }

  notifyError(error: Error): void {
    const result = { success: false, error } as const;
    this.result = result;
    this.listeners.forEach((listener) => listener(result));
  }

  readSuspended(): T {
    if (this.result === null) {
      throw new Promise((resolve, reject) => {
        const func = (result: Result<T>): void => {
          this.unsubscribe(func);
          if (result.success) {
            resolve(null);
          } else {
            reject(result.error);
          }
        };

        this.subscribe(func);
      });
    }

    if (this.result.success) {
      return this.result.data;
    } else {
      throw this.result.error;
    }
  }
}
